import React from 'react';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';

import { DataAPIContext, CREATE, GET_ONE } from '../api';
import {
    BackgroundTaskContext,
    isBackgroundTaskResponse,
    notifyBackgroundTask,
    useTaskCallback,
} from '../bg_tasks';
import sharedContext from '../sharedContext';
import downloadBlobResponse, { downloadFromUrl } from './downloadBlobResponse';

/**
 * Export a server-side data grid, honouring its current filters and selection.
 *
 * Meant to be reusable across grids — the Users grid is just the first caller.
 * Give it the same filter/query props the grid gives RecordListPro and it
 * replays them onto `POST /<resource>/export/`, so the file contains exactly
 * the rows on screen.
 *
 * Two outcomes, decided by the server on size:
 *   200 → a file body, downloaded immediately.
 *   202 → a background task; we raise the usual "started" toast, then start
 *         the download when it finishes and raise a long-lived toast carrying
 *         the link as well. The same file also appears in the Background
 *         Tasks dialog on its own.
 *
 * Params mirror RecordListPro's:
 *   resource      API resource name, e.g. 'user'
 *   apiFilters    the grid's filter array (memoize it — see lessons #4)
 *   query         free-text search
 *   extraParams   extra query params the list sends
 *   urlSuffix     the list's urlSuffix, e.g. '?include_fake=true'
 *   selectedIds   ticked row ids; empty means "everything matching"
 *   isAllSelected true when the user chose "select all matching". The id list
 *                 is capped (select_all_ids returns at most MAX_SELECT_ALL), so
 *                 sending it would silently truncate the file — send the
 *                 filters instead and let the server resolve the full set.
 *   filenameFallback  base name (NO extension) used only when the server's
 *                 Content-Disposition is unreadable
 *   formats       wire names the backend accepts, default ['csv', 'xlsx']
 */
const useGridExport = ({
    resource,
    apiFilters,
    query,
    extraParams,
    urlSuffix,
    selectedIds,
    isAllSelected,
    filenameFallback,
}) => {
    const dataAPI = React.useContext(DataAPIContext);
    const bgCtx = React.useContext(BackgroundTaskContext);
    const onTaskDone = useTaskCallback();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [exporting, setExporting] = React.useState(false);

    // Once the task completes, swap its artifact for a presigned URL and offer
    // it. The list serializer only carries `has_artifacts`, not the artifact
    // array, so the names come from the detail endpoint.
    const offerDownload = React.useCallback(async (task) => {
        // The provider fires this on ANY terminal state, failure included — and
        // the export's own row-cap guard fails the task deliberately, so this
        // is the path that has to carry that message to the user rather than
        // reporting "produced no file".
        const STATE_COMPLETED = 2;
        const STATE_CANCELLED = 4;
        if (task.state === STATE_CANCELLED) {
            enqueueSnackbar('Export cancelled.', { variant: 'info' });
            return;
        }
        if (task.state !== STATE_COMPLETED) {
            enqueueSnackbar(task.error_message || 'Export failed.',
                { variant: 'error', autoHideDuration: 10000 });
            return;
        }
        try {
            const detail = await dataAPI(GET_ONE, 'bg_tasks', { id: task.id });
            const artifacts = (detail && detail.data && detail.data.artifacts) || [];
            if (!artifacts.length) {
                enqueueSnackbar('Export finished but produced no file.', { variant: 'warning' });
                return;
            }
            const artifact = artifacts[0];
            const signed = await dataAPI(GET_ONE, 'bg_tasks', {
                urlSuffix: `${task.id}/artifact_url/?name=${encodeURIComponent(artifact.name)}`,
            });
            const url = signed && signed.data && signed.data.url;
            if (!url) {
                enqueueSnackbar('Export finished but the download link expired.', { variant: 'warning' });
                return;
            }
            // Document exports flag external IDs that appear more than once in
            // the file — re-importing such a file is ambiguous (the importer
            // keeps only the most recent occurrence of each ID). Surfaced as a
            // second, warning toast beside the download one; the full list
            // (with record links) is in the task log under Background Tasks.
            if (detail.data.task_type === 'entity_export') {
                try {
                    const resultRes = await dataAPI(GET_ONE, 'bg_tasks', {
                        urlSuffix: `${task.id}/result/`,
                    });
                    const dup = resultRes && resultRes.data && resultRes.data.result
                        && resultRes.data.result.duplicates;
                    if (dup && dup.group_count) {
                        const ids = (dup.groups || []).slice(0, 5)
                            .map((g) => g.custom_id).join(', ');
                        const more = (dup.groups || []).length > 5 || dup.truncated ? ', …' : '';
                        enqueueSnackbar(
                            `${dup.group_count} external ID(s) appear more than once in this `
                            + `file${ids ? ` (${ids}${more})` : ''}. If it is imported back, the `
                            + 'result for those records is ambiguous. The full list is in the '
                            + 'task log under Background Tasks.',
                            { variant: 'warning', autoHideDuration: 30000 },
                        );
                    }
                } catch (resultError) {
                    // non-fatal: the task log still carries the full list
                }
            }
            // Start the download ourselves, as the 200 path does: the user
            // clicked Export and expects a file, not a link to go and find.
            // The toast keeps a Download button for the cases where the
            // browser held the automatic one back (multiple-download
            // prompts, a blocked frame) and for a second copy.
            downloadFromUrl(url);
            enqueueSnackbar(`Your export is ready (${artifact.name}) — downloading.`, {
                variant: 'success',
                // The app-wide default is 2.5s, which is not long enough to
                // notice a link, let alone click it.
                autoHideDuration: 20000,
                action: (key) => (
                    <>
                        <Button
                            color="inherit"
                            size="small"
                            component="a"
                            href={url}
                            target="_blank"
                            rel="noopener"
                            onClick={() => closeSnackbar(key)}
                        >
                            Download
                        </Button>
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                closeSnackbar(key);
                                if (bgCtx && bgCtx.openDialogForTask) {
                                    bgCtx.openDialogForTask(task.id);
                                }
                            }}
                        >
                            View task
                        </Button>
                        <Button color="inherit" size="small" onClick={() => closeSnackbar(key)}>
                            Dismiss
                        </Button>
                    </>
                ),
            });
        } catch (error) {
            console.error('useGridExport: could not resolve the export artifact:', error);
            enqueueSnackbar('Export finished, but the download link could not be fetched. '
                + 'It is still available under Background Tasks.', { variant: 'warning' });
        }
    }, [dataAPI, enqueueSnackbar, closeSnackbar, bgCtx]);

    // The list's own query string: the backend re-runs the identical filter
    // pipeline off these, so the file matches the grid.
    const buildListParams = React.useCallback(() => {
        const params = new URLSearchParams();
        if (apiFilters && apiFilters.length) {
            params.set('filters', JSON.stringify(apiFilters));
        }
        if (query) {
            params.set('query', query);
        }
        Object.entries(extraParams || {}).forEach(([k, v]) => {
            if (v != null && v !== '') params.set(k, String(v));
        });
        // urlSuffix carries bare params the grid sends outside `filters`
        // (e.g. '?include_fake=true'); they change which rows match, so
        // they have to be replayed too.
        if (urlSuffix) {
            new URLSearchParams(urlSuffix.replace(/^\?/, '')).forEach((v, k) => {
                params.set(k, v);
            });
        }
        // Franchise scope. withFranchiseContext appends ?franchise_to= for
        // GET_LIST/GET_ONE only — a CREATE gets the licensee in the BODY,
        // where the grid filter pipeline never looks. Forwarded here so a
        // licensee-scoped grid exports the licensee's rows, not the
        // parent's. (Harmless where the grid ignores it, as the Users grid
        // does with GRID_FILTER_FRANCHISE_SCOPE = 'parent'.)
        const licenseeId = sharedContext.get('selected_licensee_id');
        if (licenseeId && !params.has('franchise_to')) {
            params.set('franchise_to', String(licenseeId));
        }
        return params;
    }, [apiFilters, query, extraParams, urlSuffix]);

    // "Select all matching" hands us a CAPPED id list — select_all_ids returns
    // at most MAX_SELECT_ALL (5,000). Sending those ids would cap the export at
    // 5,000 rows while the banner says 40,000. Send the filters and let the
    // server resolve the full set; the export endpoint has no row limit.
    const buildSelection = React.useCallback(() => (
        (isAllSelected || !(selectedIds && selectedIds.length))
            ? { mode: 'all_matching' }
            : { mode: 'ids', ids: selectedIds }
    ), [isAllSelected, selectedIds]);

    const exportGrid = React.useCallback(async (format = 'csv') => {
        setExporting(true);
        try {
            const params = buildListParams();
            const selection = buildSelection();

            // POST, so a large explicit id list is never a URL-length problem.
            const res = await dataAPI(CREATE, resource, {
                urlSuffix: `export/?${params.toString()}`,
                data: { format, selection },
            });

            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                onTaskDone(res.data.task_id, offerDownload);
                return res;
            }
            if (res.statusCode === 204) {
                enqueueSnackbar('Nothing to export — no rows match the current filters.',
                    { variant: 'info' });
                return res;
            }
            if (res.statusCode === 403) {
                enqueueSnackbar('You do not have permission to export this data.',
                    { variant: 'error' });
                return res;
            }
            if (res.statusCode !== 200) {
                enqueueSnackbar('Export failed.', { variant: 'error' });
                return res;
            }
            // Base name only — the extension always comes from the format that
            // was actually requested. A caller-supplied name that carried its
            // own extension would mislabel every other format (an XLSX saved as
            // `.csv` is a file Excel refuses to open).
            await downloadBlobResponse(res, `${filenameFallback || resource}.${format}`);
            return res;
        } catch (error) {
            console.error('useGridExport: export failed:', error);
            enqueueSnackbar('Export failed.', { variant: 'error' });
            return null;
        } finally {
            setExporting(false);
        }
    }, [
        dataAPI, resource, buildListParams, buildSelection, filenameFallback,
        enqueueSnackbar, closeSnackbar, bgCtx, onTaskDone, offerDownload,
    ]);

    /**
     * XML/JSON document export — the import-compatible format, not a
     * spreadsheet. Always a background task: 202 → toast + download link when
     * done (same artifact plumbing as the grid export).
     *
     * options.excludeHints  hint keys to leave out (default: export everything)
     * options.sourceId      Import_Source code to stamp on the file
     */
    const exportDocument = React.useCallback(async (format = 'xml', options = {}) => {
        setExporting(true);
        try {
            const params = buildListParams();
            const selection = buildSelection();
            const res = await dataAPI(CREATE, resource, {
                urlSuffix: `document_export/?${params.toString()}`,
                data: {
                    format,
                    selection,
                    hints: { exclude: options.excludeHints || [] },
                    source_id: options.sourceId || null,
                },
            });

            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                onTaskDone(res.data.task_id, offerDownload);
                return res;
            }
            if (res.statusCode === 204) {
                enqueueSnackbar('Nothing to export — no rows match the current filters.',
                    { variant: 'info' });
                return res;
            }
            if (res.statusCode === 403) {
                enqueueSnackbar('You do not have permission to export this data.',
                    { variant: 'error' });
                return res;
            }
            enqueueSnackbar('Export failed.', { variant: 'error' });
            return res;
        } catch (error) {
            console.error('useGridExport: document export failed:', error);
            enqueueSnackbar('Export failed.', { variant: 'error' });
            return null;
        } finally {
            setExporting(false);
        }
    }, [
        dataAPI, resource, buildListParams, buildSelection, enqueueSnackbar,
        closeSnackbar, bgCtx, onTaskDone, offerDownload,
    ]);

    return { exportGrid, exportDocument, exporting };
};

export default useGridExport;
